/**
 * Reusing a layer that already exists, instead of adding it again.
 *
 * Two questions this answers, both served by the API added alongside it
 * (`modules_router.py`'s `/layers/...` routes):
 *
 *   - "What can I copy into the map I'm building?" — my OWN layers, read
 *     straight out of `moduleService`'s already-loaded module list (no
 *     request needed, see `listMyLayers`), plus whatever other owners have
 *     shared with me directly (`fetchSharedWithMe`, a real request — those
 *     modules are not otherwise visible to me at all).
 *   - "Who can I share ONE of my layers with?" — `fetchLayerShare` /
 *     `setLayerShare`, the per-layer sibling of `moduleService`'s whole-module
 *     sharing, except the layer's OWNER may call it directly (see
 *     `LayerShare`'s own docstring on the server for why that is not
 *     admin-only the way module sharing is).
 *
 * Picking a candidate always re-fetches it FULL, via `fetchLayer` — a module
 * summary reduces a file-backed layer's `localData` to `true` (see
 * `services.summary_config` on the server), so the real features are never
 * sitting in the browser until this is called. Fetching fresh on pick, rather
 * than caching whatever the picker showed, also means the copy is never
 * stale relative to a layer someone edited after this dialog was opened.
 */

import type { AppModule, Layer } from '../types';
import { api } from './apiClient';
import { moduleService } from './moduleService';

/** One of the caller's own layers, found in a DIFFERENT application. */
export interface OwnLayerEntry {
  moduleId: string;
  moduleTitle: string;
  /** The summary shape — enough to list and label, not to copy in yet. */
  layer: Layer;
}

/** One layer another owner shared directly with the caller. */
export interface SharedLayerEntry {
  moduleId: string;
  moduleTitle: string;
  ownerName: string;
  layer: Layer;
}

interface ApiLayerOut {
  moduleId: string;
  layerId: string;
  layer: Layer;
}

interface ApiSharedLayerOut {
  moduleId: string;
  moduleTitle: string;
  ownerName: string;
  layer: Layer;
}

/** One layer another owner shared with the WHOLE organization. */
export interface OrgSharedLayerEntry {
  moduleId: string;
  moduleTitle: string;
  ownerName: string;
  layer: Layer;
  createdAt: string;
  updatedAt: string;
}

interface ApiOrgSharedLayerOut extends ApiSharedLayerOut {
  createdAt: string;
  updatedAt: string;
}

/** A layer's current share grant — who, which groups, plus whether the
 *  organization as a whole can see it. */
export interface LayerShareState {
  userIds: string[];
  sharedWithOrg: boolean;
  groupIds: string[];
}

/** One change a single edit made to one field — the shape a shared layer's
 *  audit trail records each field in. */
export interface SharedLayerFieldChange {
  old: unknown;
  new: unknown;
}

/** One row of a shared layer's edit history. */
export interface SharedLayerEdit {
  id: string;
  featureId: string;
  userId: string | null;
  userName: string | null;
  changes: Record<string, SharedLayerFieldChange>;
  editedAt: string;
}

/**
 * The caller's own layers, from every application they own OTHER than
 * `excludeModuleId` (the one currently open in the Builder — reusing a layer
 * that is already right there is what "Duplicate layer" is for).
 *
 * Reads `moduleService`'s already-hydrated cache rather than making a
 * request: every module the signed-in user can see was already fetched once
 * at sign-in, summaries included (see moduleService's own docstring).
 */
export function listMyLayers(ownerId: string | undefined, excludeModuleId: string): OwnLayerEntry[] {
  if (!ownerId) return [];
  const entries: OwnLayerEntry[] = [];
  for (const module of moduleService.listAllModules() as AppModule[]) {
    if (module.ownerId !== ownerId || module.id === excludeModuleId) continue;
    for (const layer of module.layers ?? []) {
      if (layer.isGroup) continue;
      entries.push({ moduleId: module.id, moduleTitle: module.title || module.name, layer });
    }
  }
  return entries;
}

export const layerLibraryService = {
  listMyLayers,

  /** One layer's FULL document, feature data included. */
  async fetchLayer(moduleId: string, layerId: string): Promise<Layer> {
    const row = await api.get<ApiLayerOut>(
      `/modules/${encodeURIComponent(moduleId)}/layers/${encodeURIComponent(layerId)}`,
    );
    return row.layer;
  },

  /** Every layer another owner shared directly with the signed-in user. */
  async fetchSharedWithMe(): Promise<SharedLayerEntry[]> {
    const rows = await api.get<ApiSharedLayerOut[]>('/modules/layers/shared-with-me');
    return rows.map((row) => ({
      moduleId: row.moduleId,
      moduleTitle: row.moduleTitle,
      ownerName: row.ownerName,
      layer: row.layer,
    }));
  },

  /** Every layer shared with any group the signed-in user belongs to. */
  async fetchSharedWithMyGroups(): Promise<SharedLayerEntry[]> {
    const rows = await api.get<ApiSharedLayerOut[]>('/modules/layers/shared-with-my-groups');
    return rows.map((row) => ({
      moduleId: row.moduleId,
      moduleTitle: row.moduleTitle,
      ownerName: row.ownerName,
      layer: row.layer,
    }));
  },

  /** Who this layer is currently shared with, which groups, and whether the
   *  whole organization can see it — pre-fills the Share dialog. */
  async fetchLayerShare(moduleId: string, layerId: string): Promise<LayerShareState> {
    return api.get<LayerShareState>(
      `/modules/${encodeURIComponent(moduleId)}/layers/${encodeURIComponent(layerId)}/share`,
    );
  },

  /** Replaces this one layer's share list, its group grants, AND its
   *  organization flag in one call — the layer owner's or an admin's own save. */
  async setLayerShare(
    moduleId: string,
    layerId: string,
    userIds: string[],
    sharedWithOrg: boolean,
    groupIds: string[],
  ): Promise<LayerShareState> {
    return api.put<LayerShareState>(
      `/modules/${encodeURIComponent(moduleId)}/layers/${encodeURIComponent(layerId)}/share`,
      { userIds, sharedWithOrg, groupIds },
    );
  },

  /** Every layer any owner has shared with the whole organization —
   *  portal-wide, including from a module the caller otherwise cannot see. */
  async listOrgSharedLayers(): Promise<OrgSharedLayerEntry[]> {
    const rows = await api.get<ApiOrgSharedLayerOut[]>('/modules/layers/shared-with-organization');
    return rows.map((row) => ({
      moduleId: row.moduleId,
      moduleTitle: row.moduleTitle,
      ownerName: row.ownerName,
      layer: row.layer,
      createdAt: row.createdAt,
      updatedAt: row.updatedAt,
    }));
  },

  /**
   * Adds one feature to a SHARED layer — write-through, not a local edit
   * waiting on the module's own Save. Only valid once a layer has been
   * shared at least once (`layer.sharedLayerId` set); the server refuses it
   * otherwise, with a message pointing back at the normal edit-and-save path.
   */
  async createSharedLayerFeature(
    moduleId: string,
    layerId: string,
    properties: Record<string, unknown>,
    geometry: GeoJSON.Geometry | null,
  ): Promise<Layer> {
    const row = await api.post<ApiLayerOut>(
      `/modules/${encodeURIComponent(moduleId)}/layers/${encodeURIComponent(layerId)}/features`,
      { properties, geometry },
    );
    return row.layer;
  },

  /** Updates one feature's attributes/geometry on a shared layer, write-through. */
  async updateSharedLayerFeature(
    moduleId: string,
    layerId: string,
    featureIndex: number,
    properties: Record<string, unknown>,
    geometry: GeoJSON.Geometry | null,
  ): Promise<Layer> {
    const row = await api.patch<ApiLayerOut>(
      `/modules/${encodeURIComponent(moduleId)}/layers/${encodeURIComponent(layerId)}/features/${featureIndex}`,
      { properties, geometry },
    );
    return row.layer;
  },

  /** Deletes one feature from a shared layer, write-through. */
  async deleteSharedLayerFeature(moduleId: string, layerId: string, featureIndex: number): Promise<Layer> {
    const row = await api.delete<ApiLayerOut>(
      `/modules/${encodeURIComponent(moduleId)}/layers/${encodeURIComponent(layerId)}/features/${featureIndex}`,
    );
    return row.layer;
  },

  /** Recent edits to a shared layer — who changed what, and when. */
  async fetchSharedLayerEdits(moduleId: string, layerId: string): Promise<SharedLayerEdit[]> {
    return api.get<SharedLayerEdit[]>(
      `/modules/${encodeURIComponent(moduleId)}/layers/${encodeURIComponent(layerId)}/edits`,
    );
  },
};
